import { useEffect, useCallback, useState } from "react";

interface KeyboardConfig {
  onSearch?: () => void;
  onEscape?: () => void;
  onHelp?: () => void;
  onNavigateUp?: () => void;
  onNavigateDown?: () => void;
  onNavigateTop?: () => void;
  onNavigateBottom?: () => void;
  onSelect?: () => void;
  onBack?: () => void;
  onEdit?: () => void;
  onSectionJump?: (section: number) => void;
  enabled?: boolean;
}

export function useKeyboard(config: KeyboardConfig) {
  const {
    onSearch,
    onEscape,
    onHelp,
    onNavigateUp,
    onNavigateDown,
    onNavigateTop,
    onNavigateBottom,
    onSelect,
    onBack,
    onEdit,
    onSectionJump,
    enabled = true,
  } = config;

  const [showHelp, setShowHelp] = useState(false);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      const target = event.target as HTMLElement;
      const isInputFocused =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;

      if (isInputFocused && event.key !== "Escape") return;

      const isModKey = event.metaKey || event.ctrlKey;

      if (isModKey && event.key === "k") {
        event.preventDefault();
        onSearch?.();
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        setShowHelp(false);
        onEscape?.();
        return;
      }

      if (event.key === "?") {
        event.preventDefault();
        setShowHelp((prev) => !prev);
        onHelp?.();
        return;
      }

      if (event.key === "j") {
        event.preventDefault();
        onNavigateDown?.();
        return;
      }

      if (event.key === "k") {
        event.preventDefault();
        onNavigateUp?.();
        return;
      }

      if (event.key === "g" && !isModKey) {
        return;
      }

      if (event.key === "G") {
        event.preventDefault();
        onNavigateBottom?.();
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        onSelect?.();
        return;
      }

      if (event.key === "h") {
        event.preventDefault();
        onBack?.();
        return;
      }

      if (event.key === "e") {
        event.preventDefault();
        onEdit?.();
        return;
      }

      if (event.key === "/") {
        event.preventDefault();
        onSearch?.();
        return;
      }

      const num = parseInt(event.key, 10);
      if (num >= 1 && num <= 9) {
        event.preventDefault();
        onSectionJump?.(num);
        return;
      }
    },
    [
      enabled,
      onSearch,
      onEscape,
      onHelp,
      onNavigateUp,
      onNavigateDown,
      onNavigateBottom,
      onSelect,
      onBack,
      onEdit,
      onSectionJump,
    ],
  );

  const handleDoubleG = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      const target = event.target as HTMLElement;
      const isInputFocused =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;

      if (isInputFocused) return;

      if (event.key === "g") {
        onNavigateTop?.();
      }
    },
    [enabled, onNavigateTop],
  );

  useEffect(() => {
    let lastGTime = 0;

    const wrappedHandler = (event: KeyboardEvent) => {
      handleKeyDown(event);

      if (event.key === "g") {
        const now = Date.now();
        if (now - lastGTime < 300) {
          handleDoubleG(event);
          lastGTime = 0;
        } else {
          lastGTime = now;
        }
      }
    };

    window.addEventListener("keydown", wrappedHandler);
    return () => window.removeEventListener("keydown", wrappedHandler);
  }, [handleKeyDown, handleDoubleG]);

  return { showHelp, setShowHelp };
}
